import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Avatar,
  Stack,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Profile } from "../pages/Profile/Profile";
import EditUserDetailsDialog from "./EditUserDetailsDialog";
import { useCookies } from "react-cookie";
import { UserContext } from "../context/UserContext";
import UserService from "../services/UserService";
import { GetFileFromServer } from "../utils/functions/GetFileFromServer";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = React.useState(false);
  const [cookies] = useCookies(["access_token"]);
  const userCtx = React.useContext(UserContext);
  const setUser = userCtx?.setUser;
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [tab, setTab] = React.useState<"upload" | "camera">("upload");
  const imageUrl = userCtx?.user?.image
    ? GetFileFromServer(userCtx.user.image)
    : undefined;
  const openChangePassword = () => {
    // Signal the embedded Profile component to open its change-password dialog
    window.dispatchEvent(new Event("open-change-password"));
  };

  // Refresh user profile (including image path) when dialog opens
  React.useEffect(() => {
    if (!open || !cookies.access_token) return;

    let cancelled = false;

    (async () => {
      try {
        const me = await UserService.getMe(cookies.access_token);
        if (cancelled || !me) return;

        const storedUserRaw = localStorage.getItem("user");
        const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
        const updated = { ...(storedUser || userCtx?.user || {}), ...me };

        // Avoid redundant context updates that can trigger fetch loops.
        if (JSON.stringify(storedUser) !== JSON.stringify(updated)) {
          localStorage.setItem("user", JSON.stringify(updated));
          setUser?.(updated as any);
        }
      } catch (e) {
        console.error("Failed to refresh profile", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, cookies.access_token, setUser]);

  // Stop camera when the dialog closes or component unmounts
  React.useEffect(() => {
    if (!open) {
      stopCamera();
    }
    return () => stopCamera();
  }, [open]);

  const resizeToDataUrl = (file: File, maxDim = 800): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("File read failed"));
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas unsupported"));
          ctx.drawImage(img, 0, 0, width, height);
          // Use JPEG to keep size low; backend accepts jpg/jpeg
          const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadImageDataUrl = async (dataUrl: string) => {
    setUploadError(null);
    try {
      setUploading(true);
      // Avatar updates must go through /api/utils/me/edit/
      await UserService.editProfile(cookies.access_token, dataUrl);
      const me = await UserService.getMe(cookies.access_token);
      const updated = { ...userCtx?.user, ...me };
      localStorage.setItem("user", JSON.stringify(updated));
      userCtx?.setUser(updated);
    } catch (err: any) {
      console.error("Image upload failed", err);
      const status = err?.response?.status;
      setUploadError(
        status === 400
          ? t("upload_bad_request") || "Unsupported image or malformed data"
          : t("upload_failed_try_again") ||
              "Upload failed. Please try a smaller image or different format.",
      );
    } finally {
      setUploading(false);
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setUploadError(
        t("camera_not_supported") || "Camera is not supported in this browser",
      );
      return;
    }
    try {
      setUploadError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch (e) {
      console.error("Unable to start camera", e);
      setUploadError(
        t("camera_permission_denied") ||
          "Could not access the camera. Please allow camera access.",
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  };

  const toggleCamera = async () => {
    if (cameraOn) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  const capturePhoto = async () => {
    try {
      if (!videoRef.current) return;
      const video = videoRef.current;
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      const canvas = document.createElement("canvas");
      const maxDim = 800;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      await uploadImageDataUrl(dataUrl);
    } catch (e) {
      console.error("Capture failed", e);
      setUploadError(t("capture_failed") || "Failed to capture image");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="profile-dialog-title"
    >
      <DialogTitle id="profile-dialog-title">
        {t("view_your_profile")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {/* Reuse existing Profile page content inside the dialog */}
            <Profile embedded />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  mt: { xs: 1, md: 4 },
                }}
              >
                {imageUrl ? (
                  <Avatar src={imageUrl} sx={{ width: 220, height: 220 }} />
                ) : (
                  <Avatar sx={{ width: 220, height: 220 }}>
                    {/* Empty fallback */}
                  </Avatar>
                )}
              </Box>
              <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                aria-label="avatar-source-tabs"
                variant="fullWidth"
              >
                <Tab value="upload" label={t("upload") || "Upload"} />
                <Tab value="camera" label={t("camera") || "Camera"} />
              </Tabs>
              {tab === "upload" && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/*"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      setUploadError(null);
                      const valid =
                        ["image/png", "image/jpeg", "image/jpg"].includes(
                          file.type,
                        ) || file.type.startsWith("image/");
                      if (!valid) {
                        setUploadError(
                          t("unsupported_image_type") ||
                            "Unsupported image type",
                        );
                        return;
                      }
                      try {
                        setUploading(true);
                        const dataUrl = await resizeToDataUrl(file, 800);
                        await uploadImageDataUrl(dataUrl);
                      } finally {
                        setUploading(false);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading
                      ? t("uploading") || "Uploading…"
                      : t("upload_image") || "Upload image"}
                  </Button>
                </>
              )}
              {tab === "camera" && (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      style={{
                        width: "100%",
                        maxWidth: 360,
                        borderRadius: 8,
                        background: "#000",
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={toggleCamera}>
                      {cameraOn
                        ? t("stop_camera") || "Stop camera"
                        : t("start_camera") || "Start camera"}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={capturePhoto}
                      disabled={uploading || !cameraOn}
                    >
                      {t("capture") || "Capture"}
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("camera_hint") ||
                      "Allow camera access, then click Capture."}
                  </Typography>
                </>
              )}
              {uploadError && (
                <Typography variant="body2" color="error">
                  {uploadError}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {t("supported_formats") || "Supported: PNG, JPG, JPEG"}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button onClick={() => setEditOpen(true)}>
              {t("edit_user_details") || "Edit user details"}
            </Button>
            <Button onClick={openChangePassword}>
              {t("change_password") || "Change password"}
            </Button>
          </Box>
          <Box>
            <Button variant="contained" onClick={onClose}>
              {t("close") || "Close"}
            </Button>
          </Box>
        </Box>
      </DialogActions>
      <EditUserDetailsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </Dialog>
  );
};

export default ProfileDialog;
